import { z } from 'zod';

import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import { AbsenceId, SiteId, WorkerId } from '../common/ids';
import { CursorQuerySchema, pageSchema } from '../common/pagination';
import {
  ABSENCE_MAX_SPAN_DAYS,
  AbsenceTypeSchema,
  localDayDiff,
  localTimeToMinutes,
  ScheduleSourceSchema,
} from '../common/schedules';

import { LocalDateSchema, LocalTimeSchema } from './attendance';
import { ScheduleAdminRefSchema, ScheduleWorkerRefSchema } from './schedules';

/**
 * 008 · ausencias, realm admin (contracts/admin-api.md §6): permisos, vacaciones, licencias y
 * descansos médicos. `schedules.read` para leer, `schedules.write` para registrar y cancelar.
 * Una ausencia no se edita: se cancela (con motivo) y se registra otra.
 */

export const AbsenceStatusSchema = z.enum(['ACTIVE', 'CANCELLED']);
export type AbsenceStatus = z.infer<typeof AbsenceStatusSchema>;

/** `GET /absences?from_day&to_day&worker_id&site_id&type&status&cursor&limit`. */
export const AbsenceListQuerySchema = CursorQuerySchema.extend({
  from_day: LocalDateSchema.optional(),
  to_day: LocalDateSchema.optional(),
  worker_id: WorkerId.optional(),
  site_id: SiteId.optional(),
  type: AbsenceTypeSchema.optional(),
  status: z.enum(['ACTIVE', 'CANCELLED', 'ALL']).default('ACTIVE'),
}).refine((q) => !q.from_day || !q.to_day || q.from_day <= q.to_day, {
  message: 'La fecha final no puede ser anterior a la inicial',
  path: ['to_day'],
});
export type AbsenceListQuery = z.infer<typeof AbsenceListQuerySchema>;

/**
 * `POST /absences`. Reglas: `start_day ≤ end_day`; `end_day − start_day ≤ 366`; con horas ⇒ solo
 * `PERMISSION`, un solo día, ambas horas y `start_time < end_time` (ausencia parcial).
 */
export const CreateAbsenceBodySchema = z
  .object({
    worker_id: WorkerId,
    type: AbsenceTypeSchema,
    start_day: LocalDateSchema,
    end_day: LocalDateSchema,
    start_time: LocalTimeSchema.optional(),
    end_time: LocalTimeSchema.optional(),
    note: z.string().trim().min(1).max(300).optional(),
  })
  .superRefine((body, ctx) => {
    const span = localDayDiff(body.start_day, body.end_day);
    if (span < 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['end_day'],
        message: 'La fecha final no puede ser anterior a la inicial',
      });
      return;
    }
    if (span > ABSENCE_MAX_SPAN_DAYS) {
      ctx.addIssue({
        code: 'custom',
        path: ['end_day'],
        message: 'Una ausencia puede durar como máximo un año',
      });
      return;
    }
    const startTime = body.start_time;
    const endTime = body.end_time;
    if (startTime === undefined && endTime === undefined) return;
    if (body.type !== 'PERMISSION') {
      ctx.addIssue({
        code: 'custom',
        path: ['start_time'],
        message: 'Solo un permiso puede ser por horas',
      });
      return;
    }
    if (span !== 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['end_day'],
        message: 'Un permiso por horas es de un solo día',
      });
      return;
    }
    if (startTime === undefined || endTime === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: [startTime === undefined ? 'start_time' : 'end_time'],
        message: 'Indica la hora de inicio y la de fin',
      });
      return;
    }
    if (localTimeToMinutes(startTime) >= localTimeToMinutes(endTime)) {
      ctx.addIssue({
        code: 'custom',
        path: ['end_time'],
        message: 'La hora de fin debe ser posterior a la de inicio',
      });
    }
  });
export type CreateAbsenceBody = z.infer<typeof CreateAbsenceBodySchema>;

/** `POST /absences/{id}/cancel`: motivo obligatorio 1–300. */
export const CancelAbsenceBodySchema = z.object({ reason: z.string().trim().min(1).max(300) });
export type CancelAbsenceBody = z.infer<typeof CancelAbsenceBodySchema>;

export const AbsenceIdParamsSchema = z.object({ id: AbsenceId });
export type AbsenceIdParams = z.infer<typeof AbsenceIdParamsSchema>;

export const AbsenceSchema = z.object({
  id: AbsenceId,
  worker: ScheduleWorkerRefSchema,
  type: AbsenceTypeSchema,
  start_day: LocalDateSchema,
  end_day: LocalDateSchema,
  start_time: LocalTimeSchema.nullable(),
  end_time: LocalTimeSchema.nullable(),
  /** Con horas (permiso parcial de un día). */
  partial: z.boolean(),
  note: z.string().nullable(),
  status: AbsenceStatusSchema,
  source: ScheduleSourceSchema,
  external_ref: z.string().nullable(),
  created_at: IsoDateTimeSchema,
  created_by: ScheduleAdminRefSchema.nullable(),
  cancelled_at: NullableIsoDateTimeSchema,
  cancelled_by: ScheduleAdminRefSchema.nullable(),
  cancel_reason: z.string().nullable(),
});
export type Absence = z.infer<typeof AbsenceSchema>;

export const AbsenceListResponseSchema = pageSchema(AbsenceSchema);
export type AbsenceListResponse = z.infer<typeof AbsenceListResponseSchema>;
